import type { ItemDetails } from '@/features/views/components/published/types';
import type { SystemFieldsSchema, SystemFieldsTabAdapter } from '../types';

interface CreatePublishedSystemFieldsAdapterOptions {
  itemDetails: ItemDetails;
  hiddenFieldIds?: readonly string[];
}

/**
 * Static mapping from SystemFieldsSchema fields to hiddenFieldIds.
 * Hoisted to module scope to avoid recreating on every isVisible() call.
 * Fields not in this map (id, externalKey, externalLink) are always visible.
 */
const SYSTEM_FIELDS_FIELD_ID_MAP: Partial<Record<keyof SystemFieldsSchema, string>> = {
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  updatedBy: 'updatedBy',
  updatedAt: 'updatedAt',
  doneDateTime: 'doneDateTime',
};

export const createPublishedSystemFieldsAdapter = ({
  itemDetails,
  hiddenFieldIds = [],
}: CreatePublishedSystemFieldsAdapterOptions): SystemFieldsTabAdapter => {
  const getFieldValue = <K extends keyof SystemFieldsSchema>(field: K): SystemFieldsSchema[K] => {
    switch (field) {
      case 'id':
        return itemDetails.id as SystemFieldsSchema[K];
      case 'createdBy':
        return itemDetails.createdBy as SystemFieldsSchema[K];
      case 'createdAt':
        return itemDetails.createdAt as SystemFieldsSchema[K];
      case 'updatedBy':
        return itemDetails.updatedBy as SystemFieldsSchema[K];
      case 'updatedAt':
        return itemDetails.updatedAt as SystemFieldsSchema[K];
      case 'doneDateTime':
        return itemDetails.doneDateTime as SystemFieldsSchema[K];
      case 'externalKey':
        return itemDetails.externalKey as SystemFieldsSchema[K];
      case 'externalLink':
        return itemDetails.externalLink as SystemFieldsSchema[K];
      case 'forecastedStartDateTime':
        return undefined as SystemFieldsSchema[K];
      case 'forecastedFinishDateTime':
        return undefined as SystemFieldsSchema[K];
      case 'latestFinishDateTime':
        return undefined as SystemFieldsSchema[K];
      default: {
        const _exhaustiveCheck: never = field;
        return _exhaustiveCheck;
      }
    }
  };

  const isEditable = (_field: keyof SystemFieldsSchema): boolean => {
    return false;
  };

  const isDisabled = (_field: keyof SystemFieldsSchema): boolean => {
    return false;
  };

  const isVisible = (field: keyof SystemFieldsSchema): boolean => {
    if (
      field === 'forecastedStartDateTime' ||
      field === 'forecastedFinishDateTime' ||
      field === 'latestFinishDateTime'
    ) {
      return false;
    }

    const fieldId = SYSTEM_FIELDS_FIELD_ID_MAP[field];
    if (fieldId === undefined) {
      return true;
    }

    return !hiddenFieldIds.includes(fieldId);
  };

  const getFieldSchema = (): SystemFieldsSchema => {
    return {
      id: itemDetails.id,
      createdBy: itemDetails.createdBy,
      createdAt: itemDetails.createdAt,
      updatedBy: itemDetails.updatedBy,
      updatedAt: itemDetails.updatedAt,
      doneDateTime: itemDetails.doneDateTime ?? undefined,
      externalKey: itemDetails.externalKey ?? undefined,
      externalLink: itemDetails.externalLink ?? undefined,
      forecastedStartDateTime: undefined,
      forecastedFinishDateTime: undefined,
      latestFinishDateTime: undefined,
    };
  };

  return {
    getFieldValue,
    isEditable,
    isDisabled,
    isVisible,
    getFieldSchema,
    showCalculatedSchedule: false,
  };
};
